import type { AppendMessage } from '@assistant-ui/react'
import type { QueryClient } from '@tanstack/react-query'

import { buildCreateMessageContent } from '@/components/chat-v2/lib/contentConverters'
import { mergePersistedAndLiveSubagentSnapshots } from '@/components/chat-v2/lib/subagentEvents'
import {
  buildAllMessagesFromV3Sync,
  buildCurrentThreadFromV3Sync,
  buildOverlayMessagesFromV3Sync,
  buildPersistedRunMessagesFromV3Sync,
  collectKnownV3SyncObjectIds,
  collectV3LiveCursor,
  previousV3Overlay,
  createEmptyV3SyncCache,
  decideWhetherToSyncV3Digest,
  getCompactionStatusFromV3Overlay,
  mergeV3SyncCache,
  resolveCurrentModelNameFromV3Sync,
  toActiveRunIdFromV3Digest,
  toChatRunStateFromV3StreamState,
  postV3Sync,
  serializeV3SyncFetch,
} from '@/components/chat-v2/lib/v3Sync'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import {
  ChatCompactionStatus,
  ChatMessage,
  ChatRunState,
  OrganizationChatSession,
  OrganizationChatSyncCache,
  OrganizationChatSyncDigest,
  OrganizationChatSyncManifest,
  OrganizationChatSyncOverlayObject,
  OrganizationChatSyncRequest,
  OrganizationChatSyncResponse,
  OrganizationChatSyncRunObject,
  OrganizationChatSyncRunRef,
  Source,
  WithFocusFiles,
  WithOverrideModelName,
  WithSelectedConnectorSources,
  WithSelectedMcpServers,
  WithViewportContext,
  WithWebSearchAnswerTool,
} from '@/types/chat-v2'
import { UUID } from '@/types/Id'

export interface OrganizationChatSyncQueryKeyArgs {
  orgId: UUID
  sessionId: UUID | ''
}

export const organizationChatSyncQueryKeys = {
  cache: (args: OrganizationChatSyncQueryKeyArgs) =>
    ['chat-session', 'organization-sync', args.orgId, args.sessionId] as const,
}

const organizationChatSessionQueryKey = (
  args: OrganizationChatSyncQueryKeyArgs,
) => ['chat-session', args.orgId, 'organization_chat', args.sessionId] as const

export interface OrganizationChatSyncResult {
  didSync: boolean
  reason: string
  session: OrganizationChatSession | null
  runState: ChatRunState
  activeRunId: UUID | null
  compactionStatus: ChatCompactionStatus
}

export type OrganizationChatCreateMessageRequest = WithOverrideModelName &
  WithWebSearchAnswerTool &
  WithFocusFiles &
  WithViewportContext &
  WithSelectedMcpServers &
  WithSelectedConnectorSources & {
    event_type: 'msg:create'
    content: string | Awaited<ReturnType<typeof buildCreateMessageContent>>
    zed_token?: string
  }

export async function sendOrganizationChatCreateMessage(
  args: {
    orgId: UUID
    sessionId: UUID
    message: Pick<AppendMessage, 'content'>
    messageParams: Omit<
      OrganizationChatCreateMessageRequest,
      'event_type' | 'content'
    >
  },
  accessToken: string,
): Promise<void> {
  const request: OrganizationChatCreateMessageRequest = {
    event_type: 'msg:create',
    content: await buildCreateMessageContent(args.message),
    ...args.messageParams,
  }

  const url = `${API_URL}/api/v1/chat/organization/${args.orgId}/sessions/${args.sessionId}/protocol/v3/messages`
  const res: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(request),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
}

export function createEmptyOrganizationChatSyncCache(): OrganizationChatSyncCache {
  return createEmptyV3SyncCache<
    OrganizationChatSyncDigest,
    OrganizationChatSyncManifest,
    OrganizationChatSyncResponse['meta_object'] extends infer T
      ? Exclude<T, null | undefined>
      : never,
    OrganizationChatSyncResponse['view_object'] extends infer T
      ? Exclude<T, null | undefined>
      : never,
    OrganizationChatSyncRunRef,
    OrganizationChatSyncRunObject,
    OrganizationChatSyncOverlayObject,
    OrganizationChatSyncOverlayObject['messages'][number],
    OrganizationChatSyncResponse['subagent_snapshot_objects'][number]
  >()
}

export function fetchOrganizationChatSessionSync(
  queryClient: QueryClient,
  args: OrganizationChatSyncQueryKeyArgs,
  accessToken: string,
  source: string = 'manual',
  streamed?: readonly ChatMessage[],
): Promise<OrganizationChatSession> {
  return serializeV3SyncFetch('organization_chat', args.sessionId, () =>
    fetchOrganizationChatSessionSyncNow(
      queryClient,
      args,
      accessToken,
      source,
      streamed,
    ),
  )
}

async function fetchOrganizationChatSessionSyncNow(
  queryClient: QueryClient,
  args: OrganizationChatSyncQueryKeyArgs,
  accessToken: string,
  source: string,
  streamed: readonly ChatMessage[] | undefined,
): Promise<OrganizationChatSession> {
  if (!args.sessionId) {
    throw new Error('Session ID is required')
  }

  const existingCache =
    queryClient.getQueryData<OrganizationChatSyncCache>(
      organizationChatSyncQueryKeys.cache(args),
    ) ?? createEmptyOrganizationChatSyncCache()

  const previous = previousV3Overlay(existingCache, streamed)
  const req: OrganizationChatSyncRequest = {
    protocol_version: 1,
    known_object_ids: collectKnownV3SyncObjectIds(existingCache),
    live_cursor: collectV3LiveCursor(previous),
  }

  console.debug('[organization-chat-sync] fetching session sync', {
    source,
    sessionId: args.sessionId,
    orgId: args.orgId,
    cachedManifestId: existingCache.digest?.manifest_id ?? null,
    cachedActiveRunId: existingCache.digest?.active_run_id ?? null,
    cachedOverlayObjectId: existingCache.digest?.overlay_object_id ?? null,
    cachedStreamState: existingCache.digest?.stream_state ?? null,
    knownObjectCount: req.known_object_ids.length,
  })

  const url = `${API_URL}/api/v1/chat/organization/${args.orgId}/sessions/${args.sessionId}/protocol/v3/sync`
  const res = await postV3Sync(url, req, accessToken)

  const syncResponse = (await res.json()) as OrganizationChatSyncResponse

  if (syncResponse.digest.session_id !== args.sessionId) {
    throw new Error(
      `Organization chat sync session mismatch: expected ${args.sessionId}, got ${syncResponse.digest.session_id}`,
    )
  }

  console.debug('[organization-chat-sync] received sync response', {
    source,
    sessionId: args.sessionId,
    manifestId: syncResponse.digest.manifest_id,
    activeRunId: syncResponse.digest.active_run_id,
    overlayObjectId: syncResponse.digest.overlay_object_id,
    streamState: syncResponse.digest.stream_state,
    returnedMetaObject: syncResponse.meta_object?.object_id ?? null,
    returnedViewObject: syncResponse.view_object?.object_id ?? null,
    returnedRunObjectCount: syncResponse.run_objects.length,
    returnedOverlayObject: syncResponse.overlay_object?.object_id ?? null,
  })

  const nextCache = mergeOrganizationChatSyncCache(
    existingCache,
    syncResponse,
    previous,
  )
  const session = buildOrganizationChatSessionFromSyncCache(nextCache)

  queryClient.setQueryData(organizationChatSyncQueryKeys.cache(args), nextCache)
  queryClient.setQueryData(organizationChatSessionQueryKey(args), session)
  return session
}

export function mergeOrganizationChatSources(
  ...sourceLists: ReadonlyArray<ReadonlyArray<Source> | null | undefined>
): Source[] {
  const sourcesById = new Map<number, Source>()

  for (const sourceList of sourceLists) {
    if (!sourceList) continue

    for (const source of sourceList) {
      sourcesById.set(source.id, source)
    }
  }

  return [...sourcesById.values()]
}

export async function syncOrganizationChatSessionIfNeeded(
  queryClient: QueryClient,
  args: OrganizationChatSyncQueryKeyArgs,
  accessToken: string,
  digest: OrganizationChatSyncDigest,
  source: string,
  streamed?: readonly ChatMessage[],
): Promise<OrganizationChatSyncResult> {
  const cache = queryClient.getQueryData<OrganizationChatSyncCache>(
    organizationChatSyncQueryKeys.cache(args),
  )
  const cachedDigest = cache?.digest ?? null

  const decision = decideWhetherToSyncV3Digest(cache, digest)

  if (!decision.shouldSync) {
    // An idle digest is a run's terminal announcement. Matching the cached
    // sync state here is legitimate (a refetch already landed the final
    // manifest), but when terminal state appears to go missing this line is
    // the proof that the digest ARRIVED and why no re-sync happened — keep
    // it visible for idle digests.
    const logMatchedDigest =
      digest.stream_state === 'idle'
        ? console.warn.bind(console)
        : console.debug.bind(console)
    logMatchedDigest(
      '[organization-chat-sync] control digest matched cached sync state',
      {
        source,
        sessionId: args.sessionId,
        manifestId: digest.manifest_id,
        activeRunId: digest.active_run_id,
        overlayObjectId: digest.overlay_object_id,
        streamState: digest.stream_state,
        reason: decision.reason,
      },
    )
    return {
      didSync: false,
      reason: decision.reason,
      session: null,
      runState: toChatRunStateFromV3StreamState(digest.stream_state),
      activeRunId: toActiveRunIdFromV3Digest(digest),
      compactionStatus: getCompactionStatusFromV3Overlay(
        cache?.overlay_objects ?? {},
        digest.overlay_object_id,
      ),
    }
  }

  console.warn(
    '[organization-chat-sync] refetching sync state after control digest',
    {
      source,
      sessionId: args.sessionId,
      cachedManifestId: cachedDigest?.manifest_id ?? null,
      cachedActiveRunId: cachedDigest?.active_run_id ?? null,
      cachedOverlayObjectId: cachedDigest?.overlay_object_id ?? null,
      cachedStreamState: cachedDigest?.stream_state ?? null,
      incomingManifestId: digest.manifest_id,
      incomingActiveRunId: digest.active_run_id,
      incomingOverlayObjectId: digest.overlay_object_id,
      incomingStreamState: digest.stream_state,
      reason: decision.reason,
    },
  )

  const session = await fetchOrganizationChatSessionSync(
    queryClient,
    args,
    accessToken,
    `${source}:${decision.reason}`,
    streamed,
  )
  const nextCache = queryClient.getQueryData<OrganizationChatSyncCache>(
    organizationChatSyncQueryKeys.cache(args),
  )
  const nextDigest = nextCache?.digest ?? digest

  return {
    didSync: true,
    reason: decision.reason,
    session,
    runState: toChatRunStateFromV3StreamState(nextDigest.stream_state),
    activeRunId: toActiveRunIdFromV3Digest(nextDigest),
    compactionStatus: getCompactionStatusFromV3Overlay(
      nextCache?.overlay_objects ?? {},
      nextDigest.overlay_object_id,
    ),
  }
}

function mergeOrganizationChatSyncCache(
  cache: OrganizationChatSyncCache,
  response: OrganizationChatSyncResponse,
  previous: ReturnType<typeof previousV3Overlay>,
): OrganizationChatSyncCache {
  return mergeV3SyncCache(cache, response, previous)
}

export function buildOrganizationChatSessionFromSyncCache(
  cache: OrganizationChatSyncCache,
): OrganizationChatSession {
  if (cache.manifest == null) {
    throw new Error('Organization chat sync manifest is unavailable')
  }

  const metaObject = cache.meta_objects[cache.manifest.meta_object_id]
  const viewObject = cache.view_objects[cache.manifest.view_object_id]
  if (!metaObject || !viewObject) {
    throw new Error(
      'Organization chat sync cache is missing meta or view objects',
    )
  }

  const runRefById = new Map<UUID, OrganizationChatSyncRunRef>(
    cache.manifest.run_refs.map((runRef) => [runRef.run_id, runRef]),
  )
  const messagesByRunId = new Map<UUID, ChatMessage[]>()

  for (const runRef of cache.manifest.run_refs) {
    const runObject = cache.run_objects[runRef.object_id]
    if (!runObject) {
      throw new Error(
        `Missing organization chat run object ${runRef.object_id}`,
      )
    }
    messagesByRunId.set(
      runRef.run_id,
      buildPersistedRunMessagesFromV3Sync({
        built: cache.built_run_messages,
        runRefById,
        runRef,
        runObject,
      }),
    )
  }

  let overlayObject: OrganizationChatSyncOverlayObject | undefined
  if (cache.manifest.overlay_object_id) {
    overlayObject = cache.overlay_objects[cache.manifest.overlay_object_id]
    if (!overlayObject) {
      throw new Error(
        `Missing organization chat overlay object ${cache.manifest.overlay_object_id}`,
      )
    }
    messagesByRunId.set(
      overlayObject.run_id,
      buildOverlayMessages(overlayObject),
    )
  }

  const currentRunId =
    overlayObject?.run_id ?? viewObject.persisted_current_run_id
  const allMessages = buildAllMessages(
    cache.manifest,
    messagesByRunId,
    overlayObject,
  )
  // Live overlay state can briefly lag the persisted session source registry
  // during reconnects/resyncs, so merge both views instead of letting overlay
  // state blank out existing citations.
  const sources = mergeOrganizationChatSources(
    viewObject.sources,
    overlayObject?.state.sources,
  )
  const subagentSnapshotsByToolCallId = mergePersistedAndLiveSubagentSnapshots(
    viewObject.subagent_snapshot_object_ids_by_tool_call_id,
    overlayObject?.subagent_snapshot_object_ids_by_tool_call_id,
    cache.subagent_snapshot_objects,
  )
  const currentThread = buildCurrentThread(
    cache.manifest,
    messagesByRunId,
    overlayObject,
    currentRunId,
  )

  const sharedUserIds =
    metaObject.shared_user_ids ?? metaObject.shared_with_user_ids ?? []

  return {
    id: metaObject.session_id,
    created_at: metaObject.created_at,
    last_modified: metaObject.last_modified,
    initial_model_name: metaObject.initial_model_name,
    initial_agent_name: metaObject.initial_agent_name,
    initial_agent_version: metaObject.initial_agent_version,
    all_messages: allMessages,
    current_thread: currentThread,
    current_run_id: currentRunId,
    subagent_snapshots_by_tool_call_id: subagentSnapshotsByToolCallId,
    current_model_name: resolveCurrentModelName(
      cache.manifest,
      cache.run_objects,
      currentRunId,
      overlayObject,
      viewObject.current_model_name,
    ),
    shared_user_ids: sharedUserIds,
    created_by_user_id: metaObject.created_by_user_id,
    organization_id: metaObject.organization_id,
    title: viewObject.title,
    focus_organization_file_ids: viewObject.focus_organization_file_ids,
    focus_user_file_ids: viewObject.focus_user_file_ids,
    sources,
    current_followups: viewObject.current_followups,
    stream_state: cache.digest?.stream_state ?? 'idle',
  }
}

function buildOverlayMessages(
  overlayObject: OrganizationChatSyncOverlayObject,
): ChatMessage[] {
  return buildOverlayMessagesFromV3Sync(overlayObject)
}

function buildAllMessages(
  manifest: OrganizationChatSyncManifest,
  messagesByRunId: Map<UUID, ChatMessage[]>,
  overlayObject: OrganizationChatSyncOverlayObject | undefined,
): ChatMessage[] {
  return buildAllMessagesFromV3Sync(manifest, messagesByRunId, overlayObject)
}

function buildCurrentThread(
  manifest: OrganizationChatSyncManifest,
  messagesByRunId: Map<UUID, ChatMessage[]>,
  overlayObject: OrganizationChatSyncOverlayObject | undefined,
  currentRunId: UUID | null,
): ChatMessage[] {
  return buildCurrentThreadFromV3Sync(
    manifest,
    messagesByRunId,
    overlayObject,
    currentRunId,
  )
}

function resolveCurrentModelName(
  manifest: OrganizationChatSyncManifest,
  runObjects: Record<string, OrganizationChatSyncRunObject>,
  currentRunId: UUID | null,
  overlayObject: OrganizationChatSyncOverlayObject | undefined,
  persistedCurrentModelName: OrganizationChatSession['current_model_name'],
): OrganizationChatSession['current_model_name'] {
  return resolveCurrentModelNameFromV3Sync(
    manifest,
    runObjects,
    currentRunId,
    overlayObject,
    persistedCurrentModelName,
  ) as OrganizationChatSession['current_model_name']
}
